import React, { createContext, useContext, useState } from "react";
import { parseRich } from "./logic";

// The review block's lookup: what each id the plan cites resolves to (a value, a fact with its source, a quote...).
export const LookupContext = createContext({});

export function Chip({ id }) {
  const L = useContext(LookupContext);
  const x = L[id];
  if (!x)
    return (
      <span className="chip missing" title="This id doesn't match any computed value or fact. Check it before confirming.">
        {id}: not found
      </span>
    );
  const cls = x.kind === "fact" ? "fact" : x.kind === "quote" ? "quote" : x.small_n ? "small" : "";
  const tip = (x.kind === "metric" ? id : `${id} · ${x.detail}`) + (x.small_n ? " · small sample: a lead, not a finding" : "");
  return <span className={`chip ${cls}`} title={tip}>{x.short}</span>;
}

export function Rich({ text }) {
  const L = useContext(LookupContext);
  return (
    <>
      {parseRich(text, L).map((s, i) =>
        s.chip ? <Chip key={i} id={s.chip} /> : s.tag ? <span key={i} className="tag opt">{s.tag}</span> : <React.Fragment key={i}>{s.text}</React.Fragment>,
      )}
    </>
  );
}

export function Refs({ ids }) {
  return (
    <>
      {(ids || []).map((id, i) => (
        <React.Fragment key={id + i}>
          {i > 0 && " "}
          <Chip id={id} />
        </React.Fragment>
      ))}
    </>
  );
}

// Text from the plan, with a pencil to edit it in place. onSave(path, newText) runs only when the text changed.
export function Field({ path, text, edited, onSave, className = "" }) {
  const [draft, setDraft] = useState(null);
  if (draft !== null)
    return (
      <div className={`field ${className}`}>
        <textarea value={draft} autoFocus onChange={(e) => setDraft(e.target.value)} aria-label="Edit text" />
        <div style={{ marginTop: 6, display: "flex", gap: 8 }}>
          <button className="primary small" onClick={() => { if (draft !== text) onSave(path, draft); setDraft(null); }}>Save</button>
          <button className="ghost small" onClick={() => setDraft(null)}>Cancel</button>
        </div>
      </div>
    );
  return (
    <div className={`field ${className}`}>
      <span className="txt"><Rich text={text} /></span>
      {edited && <> <span className="tag edited">Edited</span></>}
      <button className="pen" title="Edit" aria-label="Edit" onClick={() => setDraft(String(text ?? ""))}>✎</button>
    </div>
  );
}

export function Confirm({ checked, onChange, label = "Confirmed" }) {
  return (
    <label className="confirm">
      <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} />
      {label}
    </label>
  );
}

// A section card. Required cards carry a confirmation box and a red edge until it's ticked.
export function Card({ section, title, why, required = false, done = false, onConfirm, updated, children }) {
  return (
    <section className={`card ${required ? "req" : ""} ${done ? "done" : ""} ${updated ? "changed" : ""}`} data-section={section}>
      <h2>
        {title}
        {required && (done ? <span className="tag ok">Confirmed</span> : <span className="tag req">Needs your confirmation</span>)}
        {updated && <span className="tag upd">Updated</span>}
      </h2>
      {why && <p className="why">{why}</p>}
      {children}
      {required && onConfirm && <Confirm checked={done} onChange={onConfirm} />}
    </section>
  );
}
